"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Cpu, Layers, Maximize2, PlugZap, PackageCheck, ShieldCheck, Warehouse, Tag, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface ChecklistItem {
  id: string;
  code: string;
  title: string;
  question: string;
  iconName: string;
}
const CHECKLIST_ITEMS: ChecklistItem[] = [{
  id: "part-type",
  code: "SPEC-01",
  title: "Part Type",
  question: "What component is being sold?",
  iconName: "Cpu"
}, {
  id: "compatibility",
  code: "SPEC-02",
  title: "Compatibility Information",
  question: "Which machines or systems are specifically documented as compatible?",
  iconName: "Layers"
}, {
  id: "dimensions",
  code: "SPEC-03",
  title: "Dimensions",
  question: "Are measurements available where they matter?",
  iconName: "Maximize2"
}, {
  id: "connections",
  code: "SPEC-04",
  title: "Connections",
  question: "Are plug, connector or mounting details shown where relevant?",
  iconName: "PlugZap"
}, {
  id: "what-is-included",
  code: "SPEC-05",
  title: "What Is Included",
  question: "Does the listing include only the part, or additional cables, hardware or accessories?",
  iconName: "PackageCheck"
}, {
  id: "condition",
  code: "SPEC-06",
  title: "Condition",
  question: "Is the component new, used or otherwise described?",
  iconName: "ShieldCheck"
}, {
  id: "availability",
  code: "SPEC-07",
  title: "Availability",
  question: "Is it currently in stock?",
  iconName: "Warehouse"
}, {
  id: "price",
  code: "SPEC-08",
  title: "Price",
  question: "Is the current price clearly visible?",
  iconName: "Tag"
}];
function SpecIcon({
  name
}: {
  name: string;
}) {
  const iconProps = {
    className: "size-4 text-primary shrink-0",
    "aria-hidden": "true"
  };
  switch (name) {
    case "Cpu":
      return <Cpu {...iconProps} />;
    case "Layers":
      return <Layers {...iconProps} />;
    case "Maximize2":
      return <Maximize2 {...iconProps} />;
    case "PlugZap":
      return <PlugZap {...iconProps} />;
    case "PackageCheck":
      return <PackageCheck {...iconProps} />;
    case "ShieldCheck":
      return <ShieldCheck {...iconProps} />;
    case "Warehouse":
      return <Warehouse {...iconProps} />;
    case "Tag":
      return <Tag {...iconProps} />;
    default:
      return <CheckCircle2 {...iconProps} />;
  }
}
export default function Checklist() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="replacementpartchecklist" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="rounded-none border-border bg-muted/60 text-muted-foreground font-mono text-[11px] uppercase tracking-wider px-2 py-0.5">
              Inspection Protocol
            </Badge>
            <span className="h-px w-8 bg-border" aria-hidden="true" />
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight uppercase text-foreground leading-tight">
            What to Check on a Replacement Part Listing
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            A useful replacement-part listing should answer more than &apos;what is this?&apos; It should help you determine whether the component is worth investigating for your machine.
          </p>
        </div>

        {/* 8-Card Checklist Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {CHECKLIST_ITEMS.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants} className="group relative bg-card p-6 flex flex-col justify-between transition-[background-color,border-color] duration-200 hover:bg-muted/40">
              <div>
                {/* Header row: Index code and icon */}
                <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-border/70">
                  <span className="font-mono text-xs text-muted-foreground font-semibold tracking-wider">
                    {item.code}
                  </span>
                  <div className="size-8 rounded-none border border-border bg-background flex items-center justify-center transition-colors group-hover:border-primary/50">
                    <SpecIcon name={item.iconName} />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-lg font-bold font-serif uppercase tracking-tight text-card-foreground group-hover:text-primary transition-colors">
                  {item.title}
                </h3>

                {/* Supporting Question */}
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {item.question}
                </p>
              </div>

              {/* Status indicator bar */}
              <div className="mt-6 pt-3 border-t border-border/40 flex items-center justify-between text-xs font-mono text-muted-foreground/80">
                <span className="uppercase text-[11px] tracking-wide">Standard Requirement</span>
                <span className="size-1.5 bg-primary/70 rounded-full" aria-hidden="true" />
              </div>
            </motion.div>)}
        </motion.div>

        {/* Grounded Closing Callout Banner */}
        <div className="mt-8 border border-border bg-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-3.5 max-w-3xl">
            <div className="size-6 shrink-0 mt-0.5 rounded-none border border-primary/40 bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="size-3.5 text-primary" aria-hidden="true" />
            </div>
            <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">
              When one of these details is unknown, the listing should say so rather than filling the gap with a generic specification.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild variant="outline" className="rounded-none border-border font-mono text-xs uppercase tracking-wider h-10 px-5 w-full sm:w-auto hover:bg-accent">
              <Link to="/MechanicalBullPartsAndAccessoriesPage">
                View Parts Catalog
                <ArrowRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}